/**
 * V52.10: SeatMap UI 상태 Context
 *
 * 목적: nodes 배열에서 UI 상태(isEditMode, duplicateSeatCodes)를 분리하여
 * 클릭/선택 시 nodes 재생성을 방지하고 무한 루프를 제거합니다.
 *
 * 핵심 원칙:
 * - nodes 구조(position, size 등)와 UI 상태(선택, 편집모드, 중복경고)를 분리
 * - SeatMapObjectNode는 Context에서 UI 상태를 읽음
 * - nodes 동기화는 objects(SSoT) 변경 시에만 발생
 *
 * @module pages/organization/components/seatMap/SeatMapContext
 */

import React, { createContext, useContext, useMemo } from 'react';

// ============================================
// Context 타입 정의
// ============================================

interface SeatMapContextValue {
  /** 편집 모드 여부 */
  isEditMode: boolean;
  /** 중복 좌석 코드 Set */
  duplicateSeatCodes: Set<string>;
}

const SeatMapContext = createContext<SeatMapContextValue | null>(null);

// ============================================
// Provider 컴포넌트
// ============================================

interface SeatMapProviderProps {
  isEditMode: boolean;
  duplicateSeatCodes: Set<string>;
  children: React.ReactNode;
}

export function SeatMapProvider({
  isEditMode,
  duplicateSeatCodes,
  children,
}: SeatMapProviderProps) {
  // 메모이제이션: isEditMode와 duplicateSeatCodes 참조가 바뀌어도
  // 실제 값이 같으면 Context value 참조 유지
  const value = useMemo<SeatMapContextValue>(
    () => ({
      isEditMode,
      duplicateSeatCodes,
    }),
    [isEditMode, duplicateSeatCodes]
  );

  return (
    <SeatMapContext.Provider value={value}>
      {children}
    </SeatMapContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useSeatMapContext(): SeatMapContextValue {
  const ctx = useContext(SeatMapContext);
  if (!ctx) {
    throw new Error('useSeatMapContext must be used within SeatMapProvider');
  }
  return ctx;
}
